import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';
import { PaneWindow } from './components/PaneWindow.js';
import { paneFromLocation } from './lib/panes.js';
import { SessionProvider } from './lib/session.js';
import { MarketFeedProvider } from './lib/marketFeed.js';
import { ScriptsProvider } from './lib/scripts.js';
import { ErrorBoundary } from './components/ErrorBoundary.js';
import { flushDesktopPanics, installGlobalHandlers } from './lib/reporting.js';
// The design system, in load order. `semantic.css` is shared with mobile and
// carries colour; `metrics.web.css` is web/desktop only and carries size. The
// split is why a phone can share this product's identity without sharing a
// desktop density grid — see docs/18 §7.
import '@aegis/ui/fonts.css';
import '@aegis/ui/semantic.css';
import '@aegis/ui/typography.css';
import '@aegis/ui/metrics.web.css';
import '@aegis/ui/motion.css';
import '@aegis/ui/chart.tokens.css';
import '@aegis/ui/reset.web.css';
import './styles/app.css';

const el = document.getElementById('root');
if (!el) throw new Error('#root not found');
// Before the first render, deliberately. An error thrown while the app is
// mounting is the one most worth catching, and a listener attached afterwards
// would be too late for it.
installGlobalHandlers();

// Panics the Tauri shell recorded before it died, delivered by the launch
// that follows the crash. Not awaited: this is the only report in the system
// that is already old, and holding the first render for it would trade a
// visible delay for nothing.
void flushDesktopPanics();

/**
 * A detached panel, when the shell opened this window as one.
 *
 * Decided before anything renders rather than inside the router: a pane window
 * has no rail, no switcher and no route history, and mounting the whole app
 * first would flash the full interface into a 340px window before replacing it.
 *
 * Read once. A pane window's identity does not change over its life — the
 * shell opens a new window instead — so listening for hash changes here would
 * only add a way for a window to quietly become a different panel.
 */
const pane = paneFromLocation(globalThis.location.hash);

createRoot(el).render(
  <StrictMode>
    {/* Outside SessionProvider, for the same reason the native app's boundary
        sits outside its providers: a boundary that needs a context cannot
        report an error thrown while that context is mounting. */}
    <ErrorBoundary>
      <SessionProvider>
        {/* Inside the session, because the socket authenticates as whoever is
            signed in, and outside both surfaces, because a detached pane
            window draws live prices too. */}
        <MarketFeedProvider>
          {/* Around the main surface only. A detached pane window draws no
              chart, so it has no studies to run, and fetching the script list
              there would be a request per window for a list nothing reads. */}
          {pane
            ? <PaneWindow kind={pane.kind} symbol={pane.symbol} />
            : <ScriptsProvider><App /></ScriptsProvider>}
        </MarketFeedProvider>
      </SessionProvider>
    </ErrorBoundary>
  </StrictMode>,
);
